<template>
  <div v-if="options.length > 0" class="product-variant-picker">
    <div
      v-if="$textDictionary['pdp.configuration.title.label']"
      class="product-variant-picker__title"
    >
      <span>
        {{ $textDictionary['pdp.configuration.title.label'] }}
      </span>
    </div>
    <div class="product-variant-picker__items">
      <div
        v-for="optionGroup in options"
        :key="optionGroup.code!"
        class="product-variant-picker__item"
      >
        <div class="product-variant-picker__option-groups">
          <ProductVariantPickerOptionGroup v-bind="optionGroup" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductVariantPickerOptionGroup } from '@hypercodestudio/basler-components';
import type { Props as VariantOptionProps } from '@hypercodestudio/basler-components/dist/components/modules/ProductVariantPickerOption.vue';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { isDefined } from '~/utils/guards/isDefined';
import { type ParentVariantFragment } from '~/lib/Shop/generated/schema';

const store = useProductDetailPageStore();
const { $textDictionary } = useNuxtApp();
const locale = useLocale();

const options = computed(() =>
  store.options
    .filter(
      (optionFragment) =>
        optionFragment.values?.length && optionFragment.values.length > 1
    )
    .map((optionFragment) => {
      return {
        code: optionFragment.attribute_code,
        title:
          $textDictionary.value[
            'pdp.configuration.option-group.label'
          ]?.replace('{attribute}', optionFragment.label ?? '') ??
          optionFragment.label ??
          '',
        options:
          optionFragment.values
            ?.filter(isDefined)
            .map<VariantOptionProps & { price: number }>((value) => {
              let url_key = findMatchingProduct(value.uid)?.url_key ?? '#';
              const price =
                findMatchingProduct(value.uid)?.price_range.minimum_price
                  .final_price.value ?? 0;

              if (
                optionFragment.position == 0 &&
                url_key === '#' &&
                typeof value.uid === 'string'
              ) {
                url_key = findNextMatchingUrlKey(value.uid)?.url_key ?? '#';
              }

              // Das eigentliche Objekt, das später bearbeitet wird
              const option = {
                link: {
                  uri: buildPdpUrl(locale.value!, url_key)
                },
                id: value.uid ?? '',
                label: value.label ?? '',
                value: value.uid ?? '',
                price: price,
                isActive:
                  value.uid != null &&
                  store.currentVariantOptionUids.includes(value.uid),
                isDisabled:
                  store.loading ||
                  (isDefined(optionFragment) &&
                    isDefined(optionFragment.position) &&
                    optionFragment?.position > 0 &&
                    findMatchingVariant(
                      value.uid,
                      store.currentVariantOptionUids,
                      store.parentProduct?.variants
                    )?.product?.url_key == null)
              };

              return option;
            })
            .sort((a, b) => {
              if (a.price === 0) {
                return 1;
              }
              if (b.price === 0) {
                return -1;
              }

              if (a.price !== b.price) {
                return a.price - b.price;
              } else {
                return a.label.localeCompare(b.label);
              }
            })
            .map((option) => {
              const { price, ...optionWithoutPrice } = option;
              return optionWithoutPrice;
            }) ?? []
      };
    })
);

function findMatchingVariant(
  newOptionUid: string | null | undefined,
  currentVariantOptionUids: ReadonlyArray<string>,
  variants:
    | ReadonlyArray<ParentVariantFragment | undefined | null>
    | undefined
    | null
): ParentVariantFragment | undefined {
  if (!newOptionUid || !store.currentVariant) {
    // XXX: throw error or hide option?
    return;
  }

  // contains the remaining static options without the new option uid.
  const staticOptionUids =
    currentVariantOptionUids?.filter((uid) => uid !== newOptionUid) ?? [];
  return variants?.filter(isDefined).find((variant) => {
    const attributes = variant?.attributes?.filter(isDefined);
    if (!attributes) {
      return false;
    }

    const optionUids = attributes.map((attr) => attr.uid).filter(isDefined);
    if (!optionUids.includes(newOptionUid)) {
      return false;
    }

    // the remaining options should match. One different option is ok as we
    // check that the new newOptionUid is always the different option.
    return (
      optionUids.filter((uid) => !staticOptionUids.includes(uid)).length === 1
    );
  });
}

function findMatchingProduct(newOptionUid: string | null | undefined) {
  return findMatchingVariant(
    newOptionUid,
    store.currentVariantOptionUids,
    store.parentProduct?.variants
  )?.product;
}

function findNextMatchingUrlKey(newFirstOption: string) {
  const variantsWithNewFirstOption =
    store.parentProduct?.variants?.filter(
      (variant) => variant?.attributes?.[0]?.uid == newFirstOption
    ) ?? [];

  for (let counter = 1; counter < store.options.length; counter++) {
    for (const variant of variantsWithNewFirstOption) {
      if (
        isDefined(variant?.product) &&
        store.currentVariantOptionUids[counter] ==
          variant?.attributes?.[counter]?.uid
      ) {
        return variant.product;
      }
    }
  }
  return variantsWithNewFirstOption[0]?.product;
}
</script>
